import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Paper, 
  Grid, 
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { blogPostAPI } from '../api/api';
import { isAdmin } from '../utils/auth';
import dayjs from 'dayjs';

interface BlogPost {
  _id: string;
  title: string;
  content: string;
  author: string;
  tags: string[];
  publishDate: string;
}

const Blog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [currentPost, setCurrentPost] = useState<Partial<BlogPost>>({});
  const [newTag, setNewTag] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      const data = await blogPostAPI.getBlogPosts();
      setPosts(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching blog posts:', err);
      setError('Failed to load blog posts. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (post?: BlogPost) => {
    if (post) {
      setCurrentPost(post);
    } else {
      setCurrentPost({
        title: '',
        content: '',
        author: '',
        tags: [],
        publishDate: new Date().toISOString(),
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentPost({});
  };

  const handleSavePost = async () => {
    try {
      if (currentPost._id) {
        await blogPostAPI.updateBlogPost(currentPost._id, currentPost);
      } else {
        await blogPostAPI.createBlogPost(currentPost);
      }
      handleCloseDialog();
      fetchPosts();
    } catch (err) {
      console.error('Error saving blog post:', err);
      setError('Failed to save blog post. Please try again later.');
    }
  };

  const handleDeletePost = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this post?')) {
      try {
        await blogPostAPI.deleteBlogPost(id);
        fetchPosts();
      } catch (err) {
        console.error('Error deleting blog post:', err);
        setError('Failed to delete blog post. Please try again later.');
      }
    }
  };

  const handleAddTag = () => {
    if (newTag && !currentPost.tags?.includes(newTag)) {
      setCurrentPost({
        ...currentPost,
        tags: [...(currentPost.tags || []), newTag],
      });
      setNewTag('');
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setCurrentPost({
      ...currentPost,
      tags: currentPost.tags?.filter(tag => tag !== tagToRemove) || [],
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '1200px', mx: 'auto', px: { xs: 0, md: 4 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontFamily: '"Playfair Display", serif',
            fontWeight: 700,
          }}
        >
          Blog
        </Typography>
        {isAdmin() && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{
              backgroundColor: '#D4AF37',
              '&:hover': {
                backgroundColor: '#B38F2E',
              },
            }}
          >
            New Post
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      <Grid container spacing={3}>
        {posts.map((post) => (
          <Grid item xs={12} key={post._id}>
            <Paper 
              elevation={0}
              sx={{ 
                p: { xs: 2, md: 3 },
                borderRadius: { xs: 0, md: 2 },
                border: { xs: 'none', md: '1px solid' },
                borderColor: { xs: 'transparent', md: 'divider' },
                backgroundColor: 'background.paper',
                boxShadow: { xs: 'none', md: '0 4px 20px rgba(0, 0, 0, 0.05)' },
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Typography 
                  variant="h5" 
                  component="h2"
                  sx={{ 
                    fontFamily: '"Playfair Display", serif',
                    fontWeight: 600,
                  }}
                >
                  {post.title}
                </Typography>
                {isAdmin() && (
                  <Box>
                    <IconButton onClick={() => handleOpenDialog(post)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton onClick={() => handleDeletePost(post._id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                )}
              </Box>
              <Typography 
                variant="subtitle2" 
                color="text.secondary"
                sx={{ mb: 2 }}
              >
                By {post.author} • {dayjs(post.publishDate).format('MMMM D, YYYY')}
              </Typography>
              <Box sx={{ mb: 2 }}>
                {post.tags.map((tag) => (
                  <Chip 
                    key={tag} 
                    label={tag} 
                    size="small" 
                    sx={{ mr: 1, mb: 1 }}
                  />
                ))}
              </Box>
              <Box 
                sx={{ 
                  '& img': { maxWidth: '100%', height: 'auto' },
                  '& p': { fontFamily: '"Playfair Display", serif' },
                }}
                dangerouslySetInnerHTML={{ __html: post.content }}
              />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentPost._id ? 'Edit Post' : 'New Post'}
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            value={currentPost.title || ''}
            onChange={(e) => setCurrentPost({ ...currentPost, title: e.target.value })}
            margin="normal"
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Author"
            value={currentPost.author || ''}
            onChange={(e) => setCurrentPost({ ...currentPost, author: e.target.value })}
            margin="normal"
            sx={{ mb: 2 }}
          />
          <Box sx={{ mb: 2 }}>
            <TextField
              label="Add Tag"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleAddTag()}
              sx={{ mr: 1 }}
            />
            <Button onClick={handleAddTag}>Add</Button>
            <Box sx={{ mt: 1 }}>
              {currentPost.tags?.map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  onDelete={() => handleRemoveTag(tag)}
                  sx={{ mr: 1, mb: 1 }}
                />
              ))}
            </Box>
          </Box>
          <Box sx={{ height: 400, mb: 2 }}>
            <ReactQuill
              theme="snow"
              value={currentPost.content || ''}
              onChange={(content: string) => setCurrentPost({ ...currentPost, content })}
              modules={{
                toolbar: [
                  [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                  ['bold', 'italic', 'underline', 'strike'],
                  [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                  [{ 'color': [] }, { 'background': [] }],
                  ['link', 'image'],
                  ['clean']
                ],
              }}
              style={{ height: '300px' }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button 
            onClick={handleSavePost}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              '&:hover': {
                backgroundColor: '#B38F2E',
              },
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Blog; 